/*
 * BuildDocs пользовательский интерфейс version 0.1.0
 *
 * Copyright © 2021 ООО АДАПТ info@acfs.spb.ru
 * You may use, distribute and modify this code under the
 * terms of the ООО АДАПТ license
 */

import React, { useEffect, useMemo, useState } from 'react'

import { MaterialResponse, UUID, UnitLabel } from '@builddocs/data-management'
import { useDisclosure } from '@builddocs/system'
import {
  Dialog,
  Typography,
  Button,
  Stack,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  DialogContentText,
} from '@mui/material'
import Draggable from 'react-draggable'

import TableForSelect from './TableForSelect'

type Props = {
  title: string
  isSmallButton?: boolean
  allMaterials: Record<UUID, MaterialResponse>
  initialMaterials: UUID[]
  onSelect: (materialIds: UUID[]) => void
}

function PaperComponent(props) {
  return (
    <Draggable
      cancel={'[class*="MuiDialogContent-root"]'}
      handle="#draggable-dialog-title"
    >
      <Paper {...props} />
    </Draggable>
  )
}

/**
 *Диалог выбора материала
 */

function MaterialDialog(props: Props) {
  const {
    title,
    onSelect,
    isSmallButton,
    allMaterials,
    initialMaterials,
    ...rest
  } = props

  const { isOpen, onOpen, onClose } = useDisclosure()

  const [selected, setSelected] = useState<UUID[]>(initialMaterials)

  useEffect(() => setSelected(initialMaterials), [initialMaterials]) // обновления состояния в случае изменения первоначальнонго списка орг-й

  const hasValue = useMemo(() => selected.length > 0, [selected])

  const handleSelect = (materialId: UUID | UUID[]) => {
    if (Array.isArray(materialId)) {
      setSelected(materialId)
    } else {
      setSelected(prev =>
        prev.includes(materialId)
          ? prev.filter(_materialID => _materialID !== materialId)
          : [...prev, materialId],
      )
    }
  }

  const handleClose = () => {
    setSelected(initialMaterials)
    onClose()
  }

  const handleSave = () => {
    hasValue ? onSelect && onSelect(selected) : onSelect && onSelect([])
    onClose()
  }

  const handleOpen = (event: React.MouseEvent) => {
    event.preventDefault()
    onOpen()
  }

  return (
    <>
      <Stack alignItems="flex-start" {...rest}>
        {initialMaterials?.map(materialId => (
          <Typography
            key={materialId}
            color="text.secondary"
            variant="subtitle2"
          >
            {`${allMaterials[materialId].materialName} производитель ${
              allMaterials[materialId].manufacturer
            } ${allMaterials[materialId].amount} ${
              UnitLabel[allMaterials[materialId].unit]
            }`}
          </Typography>
        ))}
        <Button onClick={handleOpen}>{title}</Button>
      </Stack>
      <Dialog
        PaperComponent={PaperComponent}
        aria-labelledby="draggable-dialog-title"
        id="material-dialog-modal"
        maxWidth="md"
        open={isOpen}
        onClose={handleClose}
      >
        <DialogTitle id="draggable-dialog-title" style={{ cursor: 'move' }}>
          {title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Список материалов собран из работ, по которым сделан этот акт
          </DialogContentText>
          <TableForSelect
            items={allMaterials}
            selected={selected}
            tableType="materials"
            onSelect={handleSelect}
          />
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleClose}>
            Отмена
          </Button>
          <Button variant="contained" onClick={handleSave}>
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default MaterialDialog
